/**
 * The door to the chat's new-design skin (prest0.ai/prest0design/, design-system §7a.3).
 *
 * ON THE FIRST DOWNLOAD, so it holds nothing but the one dynamic import of the `pavilion-chat` chunk and
 * the address check. src/index.tsx calls it right after startDesignPreview(), before the first render: the
 * sheet's request is on the wire before the chat engine's (which waits for the page to paint), and it is a
 * fraction of the engine's size, so the chat arrives already dressed. Off the tree it does nothing — no
 * request, no style, no attribute (the `pavilion-chat` row of scripts/check-chat-first-paint.mjs keeps the
 * sheet itself off every prest0.ai download).
 *
 * A FAILED DOWNLOAD NEVER HOLDS THE CHAT, AND NEVER RELOADS THE PAGE: it is tried twice, and if it still
 * cannot be had the chat simply keeps today's look, working. Deliberately NOT retryImport — its last resort
 * is the stale-tab self-heal, a page reload, which is right for a chunk the app needs and wrong for a skin:
 * a person must never lose their place because a stylesheet did not arrive.
 */
import { isDesignPreview } from './previewPath';
import { preloadPavilionFont } from './pavilion/pavilionFont';

const SKIN_RETRY_DELAY_MS = 1500;

const loadSkin = () => import(/* webpackChunkName: "pavilion-chat" */ './pavilion/chat/pavilionChatSkin');

let started: Promise<void> | null = null;

/** Start loading the chat's skin, once, inside the tree. Returns the load (tests await it); null off the tree. */
export function loadPavilionChatSkin(): Promise<void> | null {
  if (started) return started;
  if (!isDesignPreview()) return null;
  preloadPavilionFont();
  started = loadSkin()
    // once more after a pause (webpack forgets a failed chunk, so the second import asks again)
    .catch(() => new Promise<void>((resolve) => setTimeout(resolve, SKIN_RETRY_DELAY_MS)).then(loadSkin))
    .then(
      (mod) => {
        mod.installPavilionChatSkin();
      },
      () => {
        /* today's look, working: a skin never holds the chat */
      },
    );
  return started;
}

/** Tests only: forget the load so the next call starts again. */
export function resetPavilionChatSkinForTests(): void {
  started = null;
}
